import { useState, useEffect } from 'react';

/**
 * Returns true when the network appears idle (no genuinely in-flight requests).
 *
 * Problem: cross-origin resources (S3 avatar images, LaunchDarkly, analytics)
 * always report responseEnd === 0 in Chromium when the server does not send a
 * Timing-Allow-Origin header — even after they have fully loaded. The original
 * code treated responseEnd === 0 as "pending", so the hook could never settle,
 * causing Gotenberg to wait for its own hard timeout (30–60 s) before capturing
 * the PDF (explaining the very slow download).
 *
 * Fix: use fetchStart to distinguish the two cases:
 *   - Cross-origin timing-blocked (done):    startTime > 0, fetchStart === 0, responseEnd === 0
 *   - Genuinely in-flight (same-origin):     startTime > 0, fetchStart > 0,  responseEnd === 0
 *
 * A resource is only counted as pending when responseEnd === 0 AND
 * (startTime === 0 OR fetchStart > 0). This correctly excludes timing-blocked
 * cross-origin entries while still waiting for real in-flight API calls.
 *
 * A hard maxWait cap (default 10 s) is kept as a safety net so Gotenberg is
 * never blocked indefinitely in unexpected edge cases.
 */
export const useNetworkIdle = (
  timeout: number = 2000,
  maxWait: number = 10000
): boolean => {
  const [isNetworkIdle, setIsNetworkIdle] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const perf = window.performance;

    const markIdle = () => {
      clearTimeout(timeoutId);
      setIsNetworkIdle(true);
    };

    // A resource is genuinely in-flight when:
    //   - responseEnd === 0 (not yet complete), AND
    //   - startTime === 0 (not yet registered) OR fetchStart > 0 (same-origin, has timing)
    // Cross-origin timing-blocked resources have startTime > 0 but fetchStart === 0,
    // so they evaluate to false and are excluded.
    const isPending = (r: PerformanceResourceTiming) =>
      r.responseEnd === 0 && (r.startTime === 0 || r.fetchStart > 0);

    const checkNetworkIdle = () => {
      const resources = perf.getEntriesByType(
        'resource'
      ) as PerformanceResourceTiming[];

      if (resources.filter(isPending).length === 0) {
        timeoutId = setTimeout(() => {
          const finalResources = perf.getEntriesByType(
            'resource'
          ) as PerformanceResourceTiming[];

          if (finalResources.filter(isPending).length === 0) {
            markIdle();
          } else {
            checkNetworkIdle();
          }
        }, timeout);
      } else {
        timeoutId = setTimeout(checkNetworkIdle, 100);
      }
    };

    // setIsNetworkIdle(false) is intentional here — we need to reset on each
    // effect run. The pre-existing lint warning is accepted for this hook.
    setIsNetworkIdle(false); // eslint-disable-line react-compiler/react-compiler
    checkNetworkIdle();

    // Safety net: force idle after maxWait so Gotenberg is never blocked forever.
    const maxWaitId = setTimeout(markIdle, maxWait);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(maxWaitId);
    };
  }, [timeout, maxWait]);

  return isNetworkIdle;
};
